import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import dayjs from "dayjs";
import { useCheckoutPaymentStatus } from "~/hooks/api/v2/checkout/useCheckoutPaymentStatus";
import { PayWithRyftButton } from "~/pages/stockList/checkoutV2/lib/PayWithRyftButton";
import { BadgeGreen, BadgeIndigo } from "../../badge/Badge";
import { StatusRow } from "./StatusRow";
import { formatCurrency } from "@sellar-io/common";

export const RyftPaymentStatus = ({
  order,
  isBuyer,
}: {
  order: OrderGetResponseType;
  isBuyer: boolean;
}) => {
  const isPaid = order.paymentStatus === "paid" || !!order.paidAt;

  const paymentStatusQuery = useCheckoutPaymentStatus(
    { orderId: order.id },
    {
      enabled: !isPaid,
      refetchInterval: (query) => {
        if (
          !isPaid &&
          (query.state.data?.status === "pending" ||
            query.state.data?.status === "succeeded")
        ) {
          return 10_000;
        }
        return false;
      },
    },
  );

  const isAccepted = paymentStatusQuery.data?.status === "succeeded";

  if (isPaid) {
    return (
      <StatusRow
        icon="CREDIT_CARD_FAST_OUTLINE"
        badge={<BadgeGreen>Paid</BadgeGreen>}
      >
        <span>
          <span className="font-semibold">Paid by card</span>
          {order.paidAt && (
            <> on {dayjs(order.paidAt).format("DD/MM/YY HH:mm")}</>
          )}
        </span>
      </StatusRow>
    );
  }

  const showAwaitingPaymentBeforeFinalisation = !isBuyer && !order.confirmedAt;

  if (showAwaitingPaymentBeforeFinalisation) {
    return (
      <StatusRow>
        <div className="flex flex-col gap-2">
          <span className="font-semibold">Awaiting card payment</span>
          <span>
            The buyer needs to complete their payment before you can finalise
            this order.
          </span>
        </div>
      </StatusRow>
    );
  }

  if (isAccepted) {
    return (
      <StatusRow
        icon="CREDIT_CARD_FAST_OUTLINE"
        badge={<BadgeIndigo>Pending</BadgeIndigo>}
      >
        <span>Payment accepted. Waiting for confirmation.</span>
      </StatusRow>
    );
  }

  if (paymentStatusQuery.isError) {
    return (
      <StatusRow>
        <div className="flex flex-col gap-2">
          <span>We couldn't refresh the payment status.</span>
          <Button
            onClick={() => paymentStatusQuery.refetch()}
            loading={paymentStatusQuery.isFetching}
          >
            Refresh status
          </Button>
        </div>
      </StatusRow>
    );
  }

  if (paymentStatusQuery.isPending) {
    return (
      <StatusRow>
        <span>Checking card payment...</span>
      </StatusRow>
    );
  }

  if (!isBuyer) {
    return (
      <StatusRow icon="CREDIT_CARD_FAST_OUTLINE">
        <span>Waiting for the retailer's card payment.</span>
      </StatusRow>
    );
  }

  return (
    <StatusRow>
      <div className="flex flex-col gap-2">
        <span className="font-semibold">Pay for this order by card</span>
        <span>
          Complete your payment to {order.Supplier.Business.name} using the
          button below.
        </span>
        <PayWithRyftButton
          orderId={order.id}
          amount={order.price}
          renderTrigger={(props) => (
            <Button {...props}>
              Pay {formatCurrency(order.price)} by card
            </Button>
          )}
        />
      </div>
    </StatusRow>
  );
};
